import type { LoSiteThemeId } from '@/lib/lyrraone-site-themes';

/** Variantes de mise en page — structure HTML distincte par thème (comme WP). */
export type LoSiteHeroLayout = 'centered' | 'split' | 'fullscreen' | 'editorial' | 'minimal' | 'bold';
export type LoSiteFeaturesLayout = 'cards' | 'strip' | 'bento' | 'minimal';
export type LoSiteHeaderLayout = 'classic' | 'centered' | 'dark' | 'minimal' | 'bar';
export type LoSiteCtaLayout = 'centered' | 'band' | 'inline';
export type LoSiteColumnsLayout = 'cards' | 'plain' | 'elevated';

export interface LoSiteThemeProfile {
  hero: LoSiteHeroLayout;
  features: LoSiteFeaturesLayout;
  header: LoSiteHeaderLayout;
  cta: LoSiteCtaLayout;
  columns: LoSiteColumnsLayout;
  /** Police titres (Google Fonts) */
  fontTitle: string;
  fontBody: string;
}

export const LO_SITE_THEME_PROFILES: Record<LoSiteThemeId, LoSiteThemeProfile> = {
  ocean: {
    hero: 'centered',
    features: 'cards',
    header: 'classic',
    cta: 'centered',
    columns: 'cards',
    fontTitle: "'Outfit', system-ui, sans-serif",
    fontBody: "'Inter', system-ui, sans-serif",
  },
  midnight: {
    hero: 'fullscreen',
    features: 'bento',
    header: 'dark',
    cta: 'band',
    columns: 'elevated',
    fontTitle: "'Outfit', system-ui, sans-serif",
    fontBody: "'Inter', system-ui, sans-serif",
  },
  teal: {
    hero: 'editorial',
    features: 'strip',
    header: 'classic',
    cta: 'centered',
    columns: 'plain',
    fontTitle: "'DM Sans', 'Outfit', system-ui, sans-serif",
    fontBody: "'Inter', system-ui, sans-serif",
  },
  violet: {
    hero: 'centered',
    features: 'bento',
    header: 'centered',
    cta: 'band',
    columns: 'elevated',
    fontTitle: "'Outfit', system-ui, sans-serif",
    fontBody: "'Inter', system-ui, sans-serif",
  },
  indigo: {
    hero: 'fullscreen',
    features: 'cards',
    header: 'dark',
    cta: 'band',
    columns: 'elevated',
    fontTitle: "'Space Grotesk', 'Outfit', system-ui, sans-serif",
    fontBody: "'Inter', system-ui, sans-serif",
  },
  ember: {
    hero: 'bold',
    features: 'strip',
    header: 'classic',
    cta: 'inline',
    columns: 'cards',
    fontTitle: "'Outfit', system-ui, sans-serif",
    fontBody: "'Inter', system-ui, sans-serif",
  },
  copper: {
    hero: 'split',
    features: 'minimal',
    header: 'bar',
    cta: 'centered',
    columns: 'plain',
    fontTitle: "'Playfair Display', Georgia, serif",
    fontBody: "'Source Sans 3', 'Inter', system-ui, sans-serif",
  },
  forest: {
    hero: 'editorial',
    features: 'cards',
    header: 'minimal',
    cta: 'centered',
    columns: 'plain',
    fontTitle: "'Outfit', system-ui, sans-serif",
    fontBody: "'Inter', system-ui, sans-serif",
  },
  rose: {
    hero: 'centered',
    features: 'cards',
    header: 'centered',
    cta: 'band',
    columns: 'elevated',
    fontTitle: "'Outfit', system-ui, sans-serif",
    fontBody: "'Inter', system-ui, sans-serif",
  },
  crimson: {
    hero: 'bold',
    features: 'strip',
    header: 'bar',
    cta: 'inline',
    columns: 'cards',
    fontTitle: "'Outfit', system-ui, sans-serif",
    fontBody: "'Inter', system-ui, sans-serif",
  },
  corporate: {
    hero: 'editorial',
    features: 'minimal',
    header: 'bar',
    cta: 'band',
    columns: 'plain',
    fontTitle: "'IBM Plex Sans', 'Outfit', system-ui, sans-serif",
    fontBody: "'IBM Plex Sans', 'Inter', system-ui, sans-serif",
  },
  magenta: {
    hero: 'bold',
    features: 'bento',
    header: 'classic',
    cta: 'band',
    columns: 'elevated',
    fontTitle: "'Outfit', system-ui, sans-serif",
    fontBody: "'Inter', system-ui, sans-serif",
  },
  mono: {
    hero: 'minimal',
    features: 'minimal',
    header: 'minimal',
    cta: 'inline',
    columns: 'plain',
    fontTitle: "'Outfit', system-ui, sans-serif",
    fontBody: "'Inter', system-ui, sans-serif",
  },
};

/** Thème utilisé pour les mises en page (hero, header, grilles) — indépendant de la palette. */
export function getLoSiteEffectiveLayoutTheme(
  siteLayoutTheme?: string | null,
  siteTheme?: string | null,
): string {
  if (siteLayoutTheme && siteLayoutTheme !== 'custom') return siteLayoutTheme;
  if (siteTheme && siteTheme !== 'custom') return siteTheme;
  return 'ocean';
}

export function getLoSiteThemeProfile(
  themeId?: string | null,
  siteLayoutTheme?: string | null,
): LoSiteThemeProfile {
  const id = getLoSiteEffectiveLayoutTheme(siteLayoutTheme, themeId) as LoSiteThemeId;
  return LO_SITE_THEME_PROFILES[id] ?? LO_SITE_THEME_PROFILES.ocean;
}

export function getLoSiteThemeProfileStyle(
  themeId?: string | null,
  siteLayoutTheme?: string | null,
): Record<string, string> {
  const profile = getLoSiteThemeProfile(themeId, siteLayoutTheme);
  return {
    '--lo-font-title': profile.fontTitle,
    '--lo-font-body': profile.fontBody,
  };
}
